/*
 * Copyright (c) All right reserved Anvish Inc. 2026
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
"use client";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@heroui/button";
import Image from "next/image";
import { Rating } from "react-simple-star-rating";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

interface TestimonialSliderProps {
  heading: string;
  testimonials: any[];
}

const SliderButtons = ({
  handlePrev,
  handleNext,
  isReachedStart,
  isReachedEnd,
}: any) => {
  return (
    <div className="flex w-full items-center justify-start gap-3 md:gap-4">
      <Button
        isIconOnly
        className="w-10 h-10 bg-black rounded-full"
        isDisabled={isReachedStart}
        radius="full"
        variant="flat"
        onPress={handlePrev}
      >
        <img
          alt="Left Arrow"
          className="w-5 h-5 object-contain rotate-180"
          height={20}
          src="/icons/arrow_left_white.svg"
          width={20}
        />
      </Button>
      <Button
        isIconOnly
        className="w-10 h-10 bg-black rounded-full"
        isDisabled={isReachedEnd}
        radius="full"
        variant="flat"
        onPress={handleNext}
      >
        <img
          alt="Left Arrow"
          className="w-5 h-5 object-contain"
          height={20}
          src="/icons/arrow_left_white.svg"
          width={20}
        />
      </Button>
    </div>
  );
};

const TestimonialSlider: React.FC<TestimonialSliderProps> = ({
  heading,
  testimonials,
}) => {
  const sliderRef = useRef<any>(null);
  const [isReachedEnd, setIsReachedEnd] = useState<boolean>(false);
  const [isReachedStart, setIsReachedStart] = useState(false);

  // Resize observer to get the height of the tallest slide
  const resizeObserver = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    // Set up the ResizeObserver
    resizeObserver.current = new ResizeObserver(() => {
      const slides = sliderRef.current?.swiper?.slides;

      if (slides) {
        let maxHeight = 0;

        // Find the tallest slide
        slides.forEach((slide: any) => {
          maxHeight = Math.max(maxHeight, slide.offsetHeight);
        });

        // Apply the height to all slides
        slides.forEach((slide: any) => {
          slide.style.height = `${maxHeight}px`;
        });
      }
    });

    // Observe the slider container
    const swiperContainer = sliderRef.current?.swiper?.wrapperEl;

    if (swiperContainer) {
      resizeObserver.current.observe(swiperContainer);
    }

    // Cleanup observer on component unmount
    return () => {
      resizeObserver.current?.disconnect();
    };
  }, []);

  const handlePrev = () => {
    sliderRef.current.swiper.slidePrev();
  };

  const handleNext = () => {
    sliderRef.current.swiper.slideNext();
  };

  // Extreme End Position Checker
  useEffect(() => {
    if (sliderRef.current?.swiper) {
      const swiper = sliderRef.current.swiper;

      setIsReachedStart(swiper.isBeginning);
      setIsReachedEnd(swiper.isEnd);

      // Listen for changes in swiper state
      swiper.on("slideChange", () => {
        setIsReachedStart(swiper.isBeginning);
        setIsReachedEnd(swiper.isEnd);
      });
    }
  }, [sliderRef]);

  return (
    <section className="w-full bg-[#F3F7FF]">
      <div className="flex flex-col lg:flex-row items-center justify-center px-4 md:px-14 xl:px-[100px] max-w-[1440px] mx-auto py-[40px] gap-10">
        <div className="flex flex-col gap-10 w-full lg:max-w-[300px]">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium">
            {heading}
          </h2>
          <div className="hidden md:block">
            <SliderButtons
              handleNext={handleNext}
              handlePrev={handlePrev}
              isReachedEnd={isReachedEnd}
              isReachedStart={isReachedStart}
            />
          </div>
        </div>
        <Swiper
          ref={sliderRef}
          breakpoints={{
            640: {
              slidesPerView: 1,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
          }}
          className="w-full h-full"
          slidesPerView={1}
          spaceBetween={20}
        >
          {testimonials.map((testimonial: any, index: any) => (
            <SwiperSlide key={index}>
              <div className="flex flex-col items-start justify-between bg-white px-5 py-10 md:px-10 md:py-[60px] gap-10 md:gap-16 h-full grow">
                <p>&ldquo;{testimonial.remark}&rdquo;</p>
                <div className="flex items-center gap-4">
                  <Image
                    alt={testimonial.user_profile_image.title}
                    className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-full"
                    height={60}
                    src={testimonial.user_profile_image.meta.download_url}
                    width={60}
                  />
                  <div className="flex flex-col gap-2">
                    <h3 className="text-normal md:text-lg font-medium">
                      {testimonial.user_name}
                    </h3>
                    <Rating
                      SVGclassName="inline-block"
                      allowHover={false}
                      fillColor="#FBD800"
                      iconsCount={5}
                      initialValue={testimonial.rating}
                      readonly={true}
                      size={24}
                    />
                  </div>
                </div>
                <div className="block md:hidden">
                  <SliderButtons
                    handleNext={handleNext}
                    handlePrev={handlePrev}
                    isReachedEnd={isReachedEnd}
                    isReachedStart={isReachedStart}
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default TestimonialSlider;
